import React from 'react';
import { motion } from 'framer-motion';
import { BOOKING_URL } from '../config';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import HeroBackground from './HeroBackground';
import StickyNote from './StickyNote';
import ScribbleUnderline from './ScribbleUnderline';
import ChatPreview from './ChatPreview';

const scrollToSection = (id) => {
  const el = document.getElementById(id);
  if (!el) return;
  window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - 80, behavior: 'smooth' });
};

const Hero = () => (
  <div className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20">
    <HeroBackground />
    
    <div className="absolute top-36 right-10 hidden xl:block animate-float-gentle" style={{ animationDelay: '0s' }}>
      <StickyNote color="yellow" rotate="3deg">
        <p className="text-[12px] font-semibold mb-1">📅 This weekend</p>
        <p className="text-[11px]">Brunch w/ Mia — 11AM</p>
        <p className="text-[11px]">Sunday: free 🌿</p>
        <p className="text-[11px] text-[#9E9690] mt-1">— Claw</p>
      </StickyNote>
    </div>
    
    <div className="absolute top-72 left-8 hidden xl:block animate-float-gentle" style={{ animationDelay: '2.5s' }}>
      <StickyNote color="pink" rotate="-2.5deg">
        <p className="text-[12px] font-semibold mb-1">🔔 Reminder set</p>
        <p className="text-[11px]">Call Mum — Sunday</p>
        <p className="text-[11px]">6PM ✓</p>
        <p className="text-[11px] text-[#881337] mt-1">auto-queued</p>
      </StickyNote>
    </div>

    <div className="relative max-w-5xl mx-auto px-6 lg:px-8 z-10 py-28 lg:py-32">
      <div className="grid lg:grid-cols-2 gap-20 items-center">
        
        <div className="flex flex-col items-start">
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.4 }}
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#EBF2F9] border border-[#C8DDE9] mb-6"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-[#3D7A5E] animate-pulse-dot" />
            <span className="text-[10px] font-semibold text-[#2D6A8F] tracking-wide uppercase font-mono-custom">
              Now available · set up in 24 hours
            </span>
          </motion.div>

          <motion.h1 
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
            className="text-4xl md:text-5xl font-extrabold tracking-tight text-[#1A1A18] leading-[1.06] mb-3"
            style={{ letterSpacing: '-0.03em' }}
          >
            Meet <span className="gradient-text-accent">Claw</span>,<br />
            your personal<br />
            <span className="gradient-text-warm">AI assistant.</span>
          </motion.h1>
          
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.55 }} className="mb-6 w-56">
            <ScribbleUnderline color="rgba(193, 123, 74, 0.35)" width={220} />
          </motion.div>

          <motion.p 
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-[15px] text-[#6B6560] max-w-lg leading-relaxed mb-8"
          >
            Claw handles your calendar, messages, reminders, travel, and daily life — so you can focus on the things that actually matter.
          </motion.p>

          <motion.div 
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto mb-5"
          >
            <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" className="btn-primary flex items-center justify-center gap-2 text-[14px] px-7 py-3 shadow-lg">
              Book a free setup call <SafeIcon icon={FiIcons.FiArrowRight} />
            </a>
            <button onClick={() => scrollToSection('how-it-works')} className="btn-secondary flex items-center justify-center gap-2 text-[14px]">
              <SafeIcon icon={FiIcons.FiPlay} className="text-[#2D6A8F]" /> See how it works
            </button>
          </motion.div>

          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.45 }} className="text-[11px] text-[#9E9690] mb-8">
            Free 20-min call · no commitment · live within 24h of setup
          </motion.p>

          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.5 }}
            className="grid grid-cols-2 gap-x-8 gap-y-3.5 w-full max-w-sm"
          >
            {[
              { icon: FiIcons.FiSmile, text: 'Feels human' },
              { icon: FiIcons.FiZap, text: 'Live in 24h' },
              { icon: FiIcons.FiShield, text: 'Your data stays yours' },
              { icon: FiIcons.FiMessageCircle, text: 'Works in WhatsApp & more' },
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-2.5">
                <div className="w-6 h-6 rounded-full bg-[#EBF2F9] border border-[#C8DDE9] flex items-center justify-center flex-shrink-0">
                  <SafeIcon icon={item.icon} className="text-[#2D6A8F] text-[11px]" />
                </div>
                <span className="text-[12px] font-medium text-[#4A4A44] leading-tight">{item.text}</span>
              </div>
            ))}
          </motion.div>
        </div>

        <motion.div 
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.3 }}
          className="flex flex-col items-center gap-6"
        >
          <ChatPreview />
          
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.8 }} className="flex items-start gap-2 px-4">
            <span className="font-handwritten text-[#C17B4A] text-[16px]" style={{ transform: 'rotate(-1deg)', display: 'inline-block' }}>
              ← this is what it's actually like
            </span>
          </motion.div>

          <div className="w-full bg-white border border-[#E8E2DA] rounded-2xl shadow-sm flex divide-x divide-[#EDE9E4]">
            {[
              { value: '24h', label: 'Setup time', sub: 'start today' },
              { value: '10h/w', label: 'Saved on avg', sub: 'per user' },
              { value: '£499', label: 'One-time fee', sub: 'no subscription' },
            ].map((s) => (
              <div key={s.value} className="text-center px-4 py-3 flex-1">
                <div className="text-lg font-bold text-[#1A1A18] tracking-tight">{s.value}</div>
                <div className="text-[10px] text-[#6B6560] mt-0.5 leading-snug">{s.label}</div>
                <div className="text-[9px] font-mono-custom text-[#9E9690] mt-0.5">{s.sub}</div>
              </div>
            ))}
          </div>
        </motion.div>

      </div>
    </div>
  </div>
);

export default Hero;